// Product carousel: arrows, optional autoplay and loop-back for the scroll-snap row.
// Settings arrive as data attributes from sections/product-carousel.liquid.
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class ProductCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;
    this.prevBtn = this.querySelector('[data-carousel-prev]');
    this.nextBtn = this.querySelector('[data-carousel-next]');
    this.autoplay = this.dataset.autoplay === 'true' && !reducedMotion.matches;
    this.delay = Number(this.dataset.delay || 4) * 1000;
    this.duration = reducedMotion.matches ? 0 : Number(this.dataset.duration || 600);
    this.loop = this.dataset.loop === 'true';
    this.byPage = this.dataset.scrollBy === 'page';
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';
    this.draggable = this.dataset.drag === 'true';
    this.paused = false;
    if (this.draggable) this.enableDrag();

    this.prevBtn?.addEventListener('click', () => { this.go(-1); this.restartAutoplay(); });
    this.nextBtn?.addEventListener('click', () => { this.go(1); this.restartAutoplay(); });
    this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });
    window.addEventListener('resize', this.onResize = () => this.updateArrows());

    if (this.autoplay) {
      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', () => { this.paused = true; });
        this.addEventListener('mouseleave', () => { this.paused = false; });
      }
      this.addEventListener('focusin', () => { this.paused = true; });
      this.addEventListener('focusout', () => { this.paused = false; });
      this.addEventListener('touchstart', () => { this.paused = true; }, { passive: true });
      this.addEventListener('touchend', () => { setTimeout(() => { this.paused = false; }, 4000); }, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibility = () => { this.paused = document.hidden; });
      this.startAutoplay();
    }
    this.updateArrows();
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
    cancelAnimationFrame(this.frame);
  }

  // Click-and-drag scrolling for mouse and pen. Touch screens already scroll natively.
  enableDrag() {
    this.track.classList.add('is-draggable');
    let startX = 0;
    let startScroll = 0;
    let moved = 0;
    let dragging = false;
    let captured = false;
    let pointerId = 0;

    this.track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      dragging = true;
      moved = 0;
      startX = event.clientX;
      startScroll = this.track.scrollLeft;
      cancelAnimationFrame(this.frame);
      pointerId = event.pointerId;
      captured = false;
      this.track.style.scrollSnapType = 'none';
      this.paused = true;
    });

    this.track.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const delta = event.clientX - startX;
      moved = Math.max(moved, Math.abs(delta));
      if (moved > 4 && !captured) {
        // Only capture the pointer once this is clearly a drag; capturing on press would
        // redirect the click away from the product link that was tapped.
        captured = true;
        this.track.setPointerCapture?.(pointerId);
        this.track.classList.add('is-dragging');
      }
      if (captured) this.track.scrollLeft = startScroll - delta;
    });

    const end = () => {
      if (!dragging) return;
      dragging = false;
      this.track.classList.remove('is-dragging');
      if (!captured) { this.track.style.scrollSnapType = ''; this.paused = false; this.restartAutoplay(); return; }
      captured = false;
      // Settle on the nearest product so the row never rests half-way between cards.
      const step = this.step / (this.byPage ? Math.max(1, Math.round(this.step / (this.track.firstElementChild?.getBoundingClientRect().width || 1))) : 1);
      const target = Math.min(Math.max(Math.round(this.position / step) * step, 0), this.maxPosition);
      this.animateTo(target);
      this.paused = false;
      this.restartAutoplay();
    };
    this.track.addEventListener('pointerup', end);
    this.track.addEventListener('pointercancel', end);

    // A drag must not count as a click on the product underneath.
    this.track.addEventListener('click', (event) => {
      if (moved > 4) { event.preventDefault(); event.stopPropagation(); moved = 0; }
    }, true);
    this.track.addEventListener('dragstart', (event) => event.preventDefault());
  }

  get sign() {
    return getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
  }

  get position() {
    return Math.abs(this.track.scrollLeft);
  }

  get maxPosition() {
    return this.track.scrollWidth - this.track.clientWidth;
  }

  get step() {
    const first = this.track.firstElementChild;
    if (!first) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const item = first.getBoundingClientRect().width + gap;
    return this.byPage ? Math.max(item, Math.floor(this.track.clientWidth / item) * item) : item;
  }

  updateArrows() {
    if (!this.prevBtn || !this.nextBtn) return;
    const atStart = this.position <= 2;
    const atEnd = this.position >= this.maxPosition - 2;
    const scrollable = this.maxPosition > 2;
    this.prevBtn.hidden = !scrollable;
    this.nextBtn.hidden = !scrollable;
    this.prevBtn.disabled = atStart && !this.loop;
    this.nextBtn.disabled = atEnd && !this.loop;
  }

  go(direction) {
    if (this.maxPosition <= 2) return;
    let target = this.position + direction * this.step;
    if (direction > 0 && this.position >= this.maxPosition - 2) {
      if (!this.loop) return;
      target = 0;
    } else if (direction < 0 && this.position <= 2) {
      if (!this.loop) return;
      target = this.maxPosition;
    }
    this.animateTo(Math.min(Math.max(target, 0), this.maxPosition));
  }

  // Custom easing so the speed setting actually controls the slide time
  // (native smooth scrolling has a fixed duration, and fights scroll-snap).
  animateTo(target) {
    cancelAnimationFrame(this.frame);
    const start = this.position;
    const distance = target - start;
    const finish = () => {
      this.track.style.scrollSnapType = '';
      this.track.scrollLeft = target * this.sign;
    };
    if (!this.duration || !distance) { finish(); return; }
    this.track.style.scrollSnapType = 'none';
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / this.duration, 1);
      const eased = p < 0.5 ? 2 * p * p : 1 - ((-2 * p + 2) ** 2) / 2;
      this.track.scrollLeft = (start + distance * eased) * this.sign;
      if (p < 1) this.frame = requestAnimationFrame(tick);
      else finish();
    };
    this.frame = requestAnimationFrame(tick);
  }

  startAutoplay() {
    clearInterval(this.timer);
    this.timer = setInterval(() => { if (!this.paused) this.go(1); }, this.delay + this.duration);
  }

  restartAutoplay() {
    if (this.autoplay) this.startAutoplay();
  }
}

if (!customElements.get('product-carousel')) {
  customElements.define('product-carousel', ProductCarousel);
}
